import { ReactNode } from 'react';

import { c } from 'ttag';

import {
    Badge,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCellProps,
    TableRow,
} from '@freelancelabs/components';
import { EyeOutline } from '@freelancelabs/icons';
import { SORT_DIRECTION } from '@freelancelabs/shared';

import { WRAPPING_HEADER } from '../../components/ListCard';
import { SortDirection } from '../../graphql/__generated__/schema';
import { AccountsQuery } from '../../graphql/__generated__/types';

export type AccountRow = NonNullable<NonNullable<AccountsQuery['accounts']>['items']>[number];

interface Props {
    rows: AccountRow[];
    moduleTotal: number | null;
    showCompany?: boolean;
    nested?: boolean;
    sortDirection?: SortDirection;
    onSortDirection?: (direction: SortDirection) => void;
    loading: boolean;
    hasError: boolean;
    state?: ReactNode;
}

// A null root signals a parentage loop, not "no company".
const companyCell = (account: AccountRow) => {
    if (account.rootOrganizationId && account.rootOrganizationId === account.id) {
        return <span className="text-text-500">{c('Accounts list').t`This account`}</span>;
    }
    if (!account.rootOrganizationName) {
        return <span className="text-text-500">{c('Accounts list').t`Company not resolved`}</span>;
    }
    return account.rootOrganizationName;
};

const countCell = (value: number | null | undefined) =>
    value === null || value === undefined ? (
        <span className="font-sans text-text-500">{c('Accounts list').t`Not counted`}</span>
    ) : (
        value
    );

const userCell = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
        return countCell(value);
    }
    return value === 0 ? <span className="text-text-500">—</span> : value;
};

const formatSiren = (siren: string) => siren.replace(/^(\d{3})(\d{3})(\d{3})$/, '$1 $2 $3');
const sirenCell = (siren: string | null | undefined) =>
    siren ? (
        <span className="inline-block rounded border border-[color:var(--portal-fiche-border)] px-1.5 py-0.5 font-mono text-xs text-text-700">
            {formatSiren(siren)}
        </span>
    ) : (
        <span className="inline-block rounded border border-dashed border-[color:var(--portal-fiche-border)] px-1.5 py-0.5 font-mono text-xs text-text-400">
            {c('Accounts list').t`SIREN —`}
        </span>
    );

const AccountsTable = ({
    rows,
    moduleTotal,
    showCompany = false,
    nested = false,
    sortDirection,
    onSortDirection,
    loading,
    hasError,
    state,
}: Props) => {
    // !important because styles is imported after Tailwind.
    const wrapping = '!whitespace-normal !break-words py-2';

    const columns: TableHeaderCellProps[] = (
        [
            {
                id: 'name',
                label: c('Accounts column').t`Account`,
                minWidth: 260,
                ...(onSortDirection && {
                    direction: sortDirection === SortDirection.Asc ? SORT_DIRECTION.ASC : SORT_DIRECTION.DSC,
                    onSort: (next: SORT_DIRECTION) =>
                        onSortDirection(next === SORT_DIRECTION.ASC ? SortDirection.Asc : SortDirection.Desc),
                }),
            },
            ...(showCompany
                ? [
                      {
                          id: 'company',
                          label: c('Accounts column').t`Head office`,
                          minWidth: 220,
                          textHelp: c('Accounts column')
                              .t`The client the account belongs to, at the top of its tree. It is shown to be read, not to be opened: it may sit outside your portfolio. An account with no parent is the top of its own tree, and says so.`,
                      },
                  ]
                : []),
            {
                id: 'siren',
                label: c('Accounts column').t`SIREN`,
                minWidth: 120,
                textHelp: c('Accounts column')
                    .t`The account's own SIREN, as ingested. It is filled on about four accounts in five, so a dash is the ordinary case and not a fault.`,
            },
            {
                id: 'reference',
                label: c('Accounts column').t`Reference`,
                minWidth: 150,
                textHelp: c('Accounts column')
                    .t`The account's Salesforce business reference (« CPT-… »), beside the technical anchor. Only accounts ingested since it was added carry it, so a dash is the ordinary case for now, not a fault.`,
            },
            { id: 'portal', label: c('Accounts column').t`Portal access`, minWidth: 130 },
            {
                id: 'users',
                label: c('Accounts column').t`Users`,
                minWidth: 90,
                textAlign: 'end',
                textHelp: c('Accounts column')
                    .t`People holding a live access on this account, invited ones included. Revoked and departed accesses are not counted: they are the history of an access, not an access.`,
            },
            {
                id: 'modules',
                label: c('Accounts column').t`Modules`,
                minWidth: 100,
                textAlign: 'end',
                textHelp: c('Accounts column')
                    .t`Modules open on this account, out of the number of entries the portal menu names. That second number is read on the module referential, never written here, and it grows with the menu.`,
            },
            { id: 'actions', label: '', minWidth: 130, textAlign: 'end' },
        ] as TableHeaderCellProps[]
    ).map((column) => ({ ...column, className: WRAPPING_HEADER }));

    return (
        <>
            <Table className="text-[13px]">
                <TableHeader resizable cells={columns} />
                <TableBody loading={loading} loadingRows={5} loadingColumns={columns.length}>
                    {(hasError ? [] : rows).map((account) => (
                        <TableRow key={account.id}>
                            <TableCell className={wrapping}>
                                <span className="flex items-center gap-2">
                                    {nested && (
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            aria-hidden="true"
                                            className="shrink-0 text-text-300"
                                        >
                                            <path d="M6 3v10a4 4 0 0 0 4 4h8" />
                                        </svg>
                                    )}
                                    <span className="font-semibold">{account.name}</span>
                                </span>
                            </TableCell>
                            {showCompany && <TableCell className={wrapping}>{companyCell(account)}</TableCell>}
                            <TableCell>{sirenCell(account.siren)}</TableCell>
                            <TableCell className="font-mono text-xs">{account.businessReference ?? '—'}</TableCell>
                            <TableCell>
                                {/* Null is not "closed": rendering it closed would announce a state nobody set. */}
                                {account.portalEnabled === null || account.portalEnabled === undefined ? (
                                    <span className="text-sm text-text-500">{c('Accounts list').t`Not stated`}</span>
                                ) : (
                                    <Badge category={account.portalEnabled ? 'success' : 'default'} size="small">
                                        {account.portalEnabled
                                            ? c('Portal access').t`• Open`
                                            : c('Portal access').t`• Closed`}
                                    </Badge>
                                )}
                            </TableCell>
                            <TableCell className="text-end font-mono text-xs">{userCell(account.userCount)}</TableCell>
                            <TableCell className="text-end font-mono text-xs">
                                {countCell(account.moduleCount)}
                                {/* Denominator only once the referential answered; "2 / 0" in flight would be a false fraction. */}
                                {moduleTotal !== null &&
                                    account.moduleCount !== null &&
                                    account.moduleCount !== undefined && (
                                        <span className="text-text-500"> / {moduleTotal}</span>
                                    )}
                            </TableCell>
                            <TableCell className="text-end">
                                <Button
                                    to={`/comptes/${account.id}`}
                                    category="weak"
                                    size="small"
                                    icon
                                    aria-label={c('Accounts').t`View the account`}
                                    title={c('Accounts').t`View the account`}
                                >
                                    <EyeOutline size={16} />
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {state}
        </>
    );
};

export default AccountsTable;
